import { TypeDefinitionAttribute } from '../codegen/graphql';

type TranslateFunction = (key: string) => string;

export type DecoratedAttribute = TypeDefinitionAttribute & {
    frontendConfig?: { [key: string]: any };
    disabled?: boolean;
};

// Pairs each registered service-data attribute (editable in the wizard) with the
// telemetry-driven counterpart on the IoTConnection that locks it once set.
export const SERVICE_DATA_COUNTERPARTS: { [registeredIndex: string]: string } = {
    registeredLastServiceMaintenance: 'lastServiceMaintenance',
    registeredLastOverhaulReManufacturing: 'lastOverhaulReManufacturing',
    registeredRunningHoursSinceLastServiceMaintenance: 'runningHoursSinceLastServiceMaintenance',
    registeredRunningHoursSinceLastOverhaulReManufacturing: 'runningHoursSinceLastOverhaulReManufacturing',
};

export const getReadOnlyServiceDataIndexes = (
    iotConnection: Record<string, unknown> | null | undefined
): string[] =>
    Object.entries(SERVICE_DATA_COUNTERPARTS)
        .filter(([, counterpart]) => iotConnection?.[counterpart] != null)
        .map(([registeredIndex]) => registeredIndex);

// Client-side validation rules for the IoT connection wizard's "Service Data" step,
// expressed as `frontendConfig.validations` descriptors understood by the shared
// TDAForm (vsm-microservice-components). The backend currently sends no rules for
// these attributes; if it ever supplies equivalent validations itself, this
// decoration can be removed.
const getServiceDataValidations = (t: TranslateFunction): { [index: string]: any[] } => ({
    registeredLastServiceMaintenance: [
        { type: 'notFuture', message: t('The date must not be in the future') },
    ],
    registeredLastOverhaulReManufacturing: [
        { type: 'notFuture', message: t('The date must not be in the future') },
        {
            type: 'beforeField',
            field: 'registeredLastServiceMaintenance',
            message: t('The date must not be after the last service/maintenance'),
        },
    ],
    registeredRunningHoursSinceLastServiceMaintenance: [
        { type: 'min', value: 0, message: t('The value must be 0 or greater') },
        {
            type: 'maxHoursSinceField',
            field: 'registeredLastServiceMaintenance',
            message: t('The value must be at most {max} hours based on the last service/maintenance date'),
        },
        {
            type: 'requiresField',
            field: 'registeredLastServiceMaintenance',
            message: t('Please set the last service/maintenance date first'),
        },
    ],
    registeredRunningHoursSinceLastOverhaulReManufacturing: [
        { type: 'min', value: 0, message: t('The value must be 0 or greater') },
        {
            type: 'maxHoursSinceField',
            field: 'registeredLastOverhaulReManufacturing',
            message: t('The value must be at most {max} hours based on the last overhaul/re-manufacturing date'),
        },
        {
            type: 'requiresField',
            field: 'registeredLastOverhaulReManufacturing',
            message: t('Please set the last overhaul/re-manufacturing date first'),
        },
    ],
});

export const decorateServiceDataAttributes = (
    attributes: TypeDefinitionAttribute[],
    t: TranslateFunction,
    readOnlyIndexes: string[] = []
): DecoratedAttribute[] => {
    const validationsByIndex = getServiceDataValidations(t);

    return attributes.map((attribute) => {
        // A registered value locked by its telemetry counterpart is passed through
        // verbatim: disabled in the form and exempt from validation, so stored
        // data can never block Finish. It still acts as a sibling reference for
        // the cross-checks of the fields that remain editable.
        if (readOnlyIndexes.includes(attribute.index)) {
            return { ...attribute, disabled: true };
        }

        const validations = validationsByIndex[attribute.index];
        if (!validations) return attribute;

        const frontendConfig = (attribute as DecoratedAttribute).frontendConfig ?? {};
        return {
            ...attribute,
            frontendConfig: { ...frontendConfig, validations },
        };
    });
};

// Mirrors the shared TDAForm checkbox "checked" coercion, so visibility decisions
// match what the rendered checkbox actually shows.
export const isGasBallastEnabled = (
    values: Record<string, unknown> | null | undefined
): boolean => {
    const value = values?.gasBallast;
    return value === true || value === 'true' || value === 1 || value === '1';
};

// Client-side rules for the wizard's "Specifications" step: the gas ballast type
// select only exists while "Gas ballast" is checked — required when shown, removed
// from form and validation when unchecked. Unchecking clears the value (via the
// requiresField descriptor), so null is sent and the type is unset.
export const decorateSpecificationAttributes = (
    attributes: TypeDefinitionAttribute[],
    gasBallastOn: boolean,
    t: TranslateFunction
): DecoratedAttribute[] =>
    attributes
        .filter((attribute) => attribute.index !== 'gasBallastType' || gasBallastOn)
        .map((attribute) => {
            if (attribute.index !== 'gasBallastType') return attribute;

            const frontendConfig = (attribute as DecoratedAttribute).frontendConfig ?? {};
            return {
                ...attribute,
                rules: [
                    ...(attribute.rules ?? []).filter((rule) => rule?.name !== 'required'),
                    { name: 'required', message: t('This field is required') },
                ],
                frontendConfig: {
                    ...frontendConfig,
                    validations: [
                        ...(frontendConfig.validations ?? []),
                        {
                            type: 'requiresField',
                            field: 'gasBallast',
                            message: t('Only available while gas ballast is enabled'),
                        },
                    ],
                },
            };
        });

// Client-side rules for the wizard's "Connection" step: the external gateway IMEI
// is enabled and required while "Connected to gateway" is checked; unchecking
// disables and clears the value, so null is sent and the gateway link is unset.
export const decorateConnectionAttributes = (
    attributes: TypeDefinitionAttribute[],
    t: TranslateFunction
): DecoratedAttribute[] =>
    attributes.map((attribute) => {
        if (attribute.index !== 'gatewayImei') return attribute;

        const frontendConfig = (attribute as DecoratedAttribute).frontendConfig ?? {};
        return {
            ...attribute,
            frontendConfig: {
                ...frontendConfig,
                validations: [
                    ...(frontendConfig.validations ?? []),
                    {
                        type: 'requiresField',
                        field: 'connectedToGateway',
                        message: t('Only available while connected to a gateway'),
                    },
                    {
                        type: 'requiredIfField',
                        field: 'connectedToGateway',
                        message: t('Required while connected to a gateway'),
                    },
                ],
            },
        };
    });
